import { useState, useEffect } from 'react';
import { authenticatedFetch } from '@/lib/auth';
import { Clock, User, FileText, AlertCircle, CheckCircle, Loader2, X } from 'lucide-react';
import { useConfirm } from './ui';

interface AccommodationTemplate {
  id: string;
  name: string;
  description: string;
  accommodation_type: string;
  default_time_multiplier: number | null;
  default_extra_time_minutes: number | null;
  requires_manual_entry: boolean;
  display_order: number;
}

interface Accommodation {
  id: string;
  template_id: string;
  template_name: string;
  accommodation_type: string;
  description: string;
  time_multiplier: number | null;
  extra_time_minutes: number | null;
  requires_manual_entry: boolean;
  created_at: string;
}

interface AccommodationManagerProps {
  // Bruk ENTEN sessionId (eksisterende exam_session) ELLER candidateId (før eksamen startet)
  sessionId?: string;
  candidateId?: string;
  candidateName?: string;
  onAccommodationSet?: (accommodation: Accommodation) => void;
}

export default function AccommodationManager({ sessionId, candidateId, candidateName, onAccommodationSet }: AccommodationManagerProps) {
  const apiPath = sessionId ? `/api/accommodations/session/${sessionId}` : `/api/accommodations/candidate/${candidateId}`;
  const modeKey = sessionId || candidateId || '';
  const { confirm } = useConfirm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [templates, setTemplates] = useState<AccommodationTemplate[]>([]);
  const [currentAccommodation, setCurrentAccommodation] = useState<Accommodation | null>(null);

  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [customTimeMultiplier, setCustomTimeMultiplier] = useState<number>(1.5);
  const [customExtraMinutes, setCustomExtraMinutes] = useState<number>(30);

  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    loadData();
  }, [modeKey]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load templates
      const templatesResponse = await authenticatedFetch('/api/accommodations/templates');
      const templatesData = await templatesResponse.json();

      if (!templatesData.success) {
        throw new Error(templatesData.error || 'Kunne ikke laste maler');
      }

      setTemplates(templatesData.templates);

      // Load current accommodation if exists
      try {
        const accommodationResponse = await authenticatedFetch(apiPath);
        const accommodationData = await accommodationResponse.json();

        if (accommodationData.success && accommodationData.accommodation) {
          setCurrentAccommodation(accommodationData.accommodation);
        }
      } catch (err) {
        // No accommodation set yet, that's fine
        console.log('No accommodation found for session');
      }

    } catch (err: any) {
      console.error('Load data error:', err);
      setError(err.message || 'Kunne ikke laste data');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setCustomDescription(template.description);
      if (template.default_time_multiplier) {
        setCustomTimeMultiplier(template.default_time_multiplier);
      }
      if (template.default_extra_time_minutes) {
        setCustomExtraMinutes(template.default_extra_time_minutes);
      }
    }
  };

  const handleSaveAccommodation = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      if (!selectedTemplateId) {
        throw new Error('Velg en mal');
      }

      const template = templates.find(t => t.id === selectedTemplateId);
      if (!template) {
        throw new Error('Ugyldig mal');
      }

      const requestBody: any = {
        template_id: selectedTemplateId,
        description: customDescription.trim() || template.description
      };

      // Add time settings based on template type
      if (template.accommodation_type === 'extra_time' || template.accommodation_type === 'screen_reader') {
        if (customTimeMultiplier && customTimeMultiplier > 1) {
          requestBody.time_multiplier = customTimeMultiplier;
        } else if (customExtraMinutes && customExtraMinutes > 0) {
          requestBody.extra_time_minutes = customExtraMinutes;
        }
      }

      const response = await authenticatedFetch(apiPath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Kunne ikke lagre tilrettelegging');
      }

      setSuccess('Tilrettelegging lagret');
      setCurrentAccommodation(data.accommodation);
      setShowAddForm(false);

      if (onAccommodationSet) {
        onAccommodationSet(data.accommodation);
      }

      setTimeout(() => setSuccess(''), 3000);

    } catch (err: any) {
      console.error('Save accommodation error:', err);
      setError(err.message || 'Kunne ikke lagre tilrettelegging');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAccommodation = async () => {
    if (!currentAccommodation) return;

    const confirmed = await confirm({
      title: 'Fjern tilrettelegging',
      message: 'Er du sikker på at du vil fjerne tilretteleggingen?',
      confirmLabel: 'Fjern',
      cancelLabel: 'Avbryt',
      variant: 'warning'
    });

    if (!confirmed) return;

    try {
      setSaving(true);
      setError('');

      const response = await authenticatedFetch(`/api/accommodations/${currentAccommodation.id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Kunne ikke fjerne tilrettelegging');
      }

      setSuccess('Tilrettelegging fjernet');
      setCurrentAccommodation(null);
      setTimeout(() => setSuccess(''), 3000);

    } catch (err: any) {
      console.error('Remove accommodation error:', err);
      setError(err.message || 'Kunne ikke fjerne tilrettelegging');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-kf-primary" />
      </div>
    );
  }

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <User className="h-5 w-5 text-kf-primary" />
            Tilrettelegging
          </h3>
          {candidateName && (
            <p className="text-sm text-gray-600">For: {candidateName}</p>
          )}
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800">{success}</p>
        </div>
      )}

      {/* Current Accommodation */}
      {currentAccommodation && !showAddForm ? (
        <div className="p-4 bg-kf-blue-50 border border-kf-blue-200 rounded-lg">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h4 className="font-semibold text-kf-blue-900">{currentAccommodation.template_name}</h4>
              <p className="text-sm text-kf-blue-800 mt-1">{currentAccommodation.description}</p>
            </div>
            <button
              onClick={handleRemoveAccommodation}
              disabled={saving}
              className="text-red-600 hover:text-red-700 p-1"
              title="Fjern tilrettelegging"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {currentAccommodation.time_multiplier && (
            <div className="flex items-center gap-2 text-sm text-kf-blue-700 mt-2">
              <Clock className="h-4 w-4" />
              <span>Ekstra tid: {((currentAccommodation.time_multiplier - 1) * 100).toFixed(0)}%</span>
            </div>
          )}

          {currentAccommodation.extra_time_minutes && (
            <div className="flex items-center gap-2 text-sm text-kf-blue-700 mt-2">
              <Clock className="h-4 w-4" />
              <span>Ekstra tid: {currentAccommodation.extra_time_minutes} minutter</span>
            </div>
          )}

          {currentAccommodation.requires_manual_entry && (
            <div className="flex items-center gap-2 text-sm text-amber-700 mt-2">
              <FileText className="h-4 w-4" />
              <span>Krever manuell sensur (muntlig prøve)</span>
            </div>
          )}

          <button
            onClick={() => setShowAddForm(true)}
            className="mt-3 text-sm text-kf-blue-600 hover:text-kf-blue-700 font-medium"
          >
            Endre tilrettelegging
          </button>
        </div>
      ) : showAddForm || !currentAccommodation ? (
        <div className="border border-gray-200 rounded-lg p-4">
          {/* Template Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Velg type tilrettelegging
            </label>
            <select
              value={selectedTemplateId}
              onChange={(e) => handleTemplateSelect(e.target.value)}
              className="input"
            >
              <option value="">-- Velg mal --</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>

          {/* Template Info */}
          {selectedTemplate && (
            <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
              <p className="text-sm text-gray-700">{selectedTemplate.description}</p>

              {selectedTemplate.default_time_multiplier && (
                <p className="text-sm text-gray-600 mt-2">
                  Standard ekstra tid: {((selectedTemplate.default_time_multiplier - 1) * 100).toFixed(0)}%
                </p>
              )}

              {selectedTemplate.requires_manual_entry && (
                <div className="flex items-center gap-2 text-sm text-amber-700 mt-2">
                  <FileText className="h-4 w-4" />
                  <span>Krever manuell sensur av sensor</span>
                </div>
              )}
            </div>
          )}

          {/* Custom Settings for Extra Time */}
          {selectedTemplate && (selectedTemplate.accommodation_type === 'extra_time' || selectedTemplate.accommodation_type === 'screen_reader') && (
            <div className="mb-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tidsmultiplikator (1.5 = 50% ekstra)
                </label>
                <input
                  type="number"
                  min="1"
                  max="3"
                  step="0.1"
                  value={customTimeMultiplier}
                  onChange={(e) => setCustomTimeMultiplier(parseFloat(e.target.value) || 1.5)}
                  className="input w-32"
                />
              </div>

              <div className="text-sm text-gray-600">eller</div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fast ekstra tid (minutter)
                </label>
                <input
                  type="number"
                  min="0"
                  step="5"
                  value={customExtraMinutes}
                  onChange={(e) => setCustomExtraMinutes(parseInt(e.target.value) || 0)}
                  className="input w-32"
                />
              </div>
            </div>
          )}

          {/* Custom Description */}
          {selectedTemplate && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Beskrivelse / Merknad
              </label>
              <textarea
                value={customDescription}
                onChange={(e) => setCustomDescription(e.target.value)}
                className="input min-h-[80px]"
                placeholder="Tilleggsinformasjon om tilretteleggingen..."
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleSaveAccommodation}
              disabled={saving || !selectedTemplateId}
              className="btn-primary flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Lagrer...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Lagre tilrettelegging
                </>
              )}
            </button>

            {currentAccommodation && (
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setSelectedTemplateId('');
                  setCustomDescription('');
                }}
                className="btn-secondary"
              >
                Avbryt
              </button>
            )}
          </div>
        </div>
      ) : null}

      {/* Show Add Button if no accommodation */}
      {!currentAccommodation && !showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          className="btn-primary w-full"
        >
          Legg til tilrettelegging
        </button>
      )}
    </div>
  );
}
